import axios from 'axios'
import { returnData, config, apiConfig } from './api-utils'

/**
 * The fields of a token request, form-encoded as the OAuth 2.0 token endpoint requires.
 *
 * Handing axios a URLSearchParams means the encoding no longer depends on its request transforms.
 * axios 0.x JSON-stringified an object body whatever the content type, so every token request used
 * to append a transform that parsed that JSON back and form-encoded it by hand. axios 1.x
 * form-encodes the object itself, the appended JSON.parse then threw on "grant_type=...", and no
 * token request left the browser (#3684).
 *
 * The fields on the wire are the ones the old transform sent: a field that is undefined is left
 * out, as JSON.stringify left it out, and anything else is sent as its string, null as "null".
 */
const formBody = (fields) =>
  new URLSearchParams(
    Object.entries(fields)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)])
  )

/**
 * POSTs a grant to the token endpoint. The headers given replace config()'s defaults instead of
 * adding to them, so a token request carries neither the stored access token nor the profile id;
 * refreshToken sends the access token it is given, and nothing else does.
 */
const requestToken = (fields, { withCredentials, headers }, apiUrl) =>
  axios
    .post(
      '/token',
      formBody(fields),
      config(
        {
          withCredentials,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...headers,
          },
        },
        apiUrl
      )
    )
    .then((response) => returnData(response))

const login = (username, password, apiUrl = apiConfig.backendUrl) =>
  requestToken(
    {
      username,
      password,
      grant_type: 'password',
      resource: apiUrl,
      scope: 'offline_access profile email',
    },
    { withCredentials: true },
    apiUrl
  )
const logout = () => {
  axios
    .post('/token/logout', {}, config({ withCredentials: true }))
    .then((response) => returnData(response))
}
// AAD/MFA logins never call the password/finvoicer `/token` grant, so they miss the
// HangfireCookie sign-in that happens as part of that flow. This lets the SPA request it
// explicitly once it has any valid access token (local or Entra).
const signInToHangfireCookie = () => {
  return axios
    .post('/token/hangfire-signin', {}, config({ withCredentials: true }))
    .then((response) => returnData(response))
}
const refreshToken = (
  access_token,
  refresh_token,
  apiUrl = apiConfig.backendUrl
) =>
  requestToken(
    {
      grant_type: 'refresh_token',
      refresh_token,
      scope: 'offline_access profile email',
    },
    {
      withCredentials: true,
      headers: { Authorization: `Bearer: ${access_token}` },
    },
    apiUrl
  )
const loginWithFinvoicerIdToken = (
  { token, challengeVerifier },
  apiUrl = apiConfig.backendUrl
) =>
  requestToken(
    {
      token,
      code_verifier: challengeVerifier,
      grant_type: 'finvoicer_id',
      scope: 'offline_access profile email',
    },
    { withCredentials: true },
    apiUrl
  )

const authApi = {
  login,
  logout,
  refreshToken,
  loginWithFinvoicerIdToken,
  signInToHangfireCookie,
}
export default authApi
